import { useQaNamespace } from '@Framework/ui/quality-assurance';
import { Chip, makeStyles } from '@material-ui/core';
import { CORPORATE_COLORS } from '@Ui/theme/colors/corporate';
import React, { CSSProperties, FC, ReactNode } from 'react';

export enum BADGE_COLOR_TYPES {
  BLUE = 'blue',
  RX = 'rx',
  RED = 'red'
}

export enum SUP_FOOTNOTE {
  PRESCRIPTION_BONUS = 7 // Exponent number i.e. <sup> for prescription bonus
}

/**
 * BadgeProp Interface
 */
export interface BadgeProps {
  /**
   * Text displayed inside the Badge
   */
  label: ReactNode;
  /**
   * Color variant for the badge component
   */
  colorVariant?: BADGE_COLOR_TYPES;
  /**
   * Whether the Badge should be displayed in outlined variant (defaults to false)
   */
  isOutlined?: boolean;
  /**
   * Font size
   */
  fontSize?: string;

  /**
   * Benefit Badge
   */
  benefits?: boolean;

  /**
   * To pass additional styling attributes
   */
  className?: string;

  /**
   * @see useQaNamespace
   */
  qaSuffix?: string;
}

const useStyles = ({
  colorVariant = BADGE_COLOR_TYPES.RED,
  fontSize,
  isOutlined
}: {
  colorVariant: BADGE_COLOR_TYPES;
  fontSize: string;
  isOutlined: boolean;
}) => {
  // if parameters are passed directly to makeStyles() they are undefined
  // a self-invoked returned makeStyles inside a function is a possible workaround:
  return makeStyles(() => {
    const root = {
      borderRadius: '19px',
      fontSize
    };
    const benefitStyle = {
      backgroundColor: CORPORATE_COLORS.BACKGROUND_LIGHT_NOW,
      color: CORPORATE_COLORS.PRIMARY,
      marginRight: '0.5rem'
    };
    const outlined = {
      backgroundColor: CORPORATE_COLORS.WHITE,
      border: '0.1rem solid',
      borderColor: CORPORATE_COLORS.PRIMARY,
      borderRadius: '48px'
    };
    const label = {
      fontSize: '12px'
    };
    const colorStyles: any = { color: CORPORATE_COLORS.WHITE };
    const map = {
      [BADGE_COLOR_TYPES.RED]: CORPORATE_COLORS.RED,
      [BADGE_COLOR_TYPES.BLUE]: CORPORATE_COLORS.BLUE,
      [BADGE_COLOR_TYPES.RX]: CORPORATE_COLORS.RX
    };
    colorStyles.backgroundColor = map[colorVariant];

    return {
      benefitStyle: {
        ...benefitStyle
      },
      label,
      root: {
        ...root,
        ...colorStyles
      },
      default: isOutlined ? outlined : {}
    };
  })();
};

const Badge: FC<BadgeProps> = ({
  qaSuffix,
  label,
  colorVariant = BADGE_COLOR_TYPES.RED,
  isOutlined = false,
  fontSize = '16px',
  className,
  benefits = false
}) => {
  const classes = useStyles({ colorVariant, fontSize, isOutlined });
  const dataQaId = qaSuffix ? useQaNamespace({ suffix: qaSuffix }) : undefined;

  return (
    <Chip
      label={<div className={classes.label}>{label}</div>}
      variant={isOutlined ? 'outlined' : 'default'}
      className={
        benefits
          ? `${classes.default} ${classes.benefitStyle} ${className}`
          : `${classes.default} ${classes.root} ${className}`
      }
      data-qa-id={dataQaId}
    />
  );
};

export { Badge };
